'use client';
import React, { useState, useEffect } from 'react';
import { ProductCard as SharedProductCard } from '@launchstore/shared-puck';
import type {
  ProductCardProps, SharedProductCardProduct, RenderProductCard,
} from '@launchstore/shared-puck';
import { ProductCard as ProductCardRenderer } from '../product-card/ProductCardRenderer';
import { retrieveProduct } from '@/lib/data/products';

const SharedProductCardRender = (SharedProductCard as any).render;

const ProductCardImpl: React.FC<ProductCardProps> = (props: any) => {
  const { productId } = props;
  const [product, setProduct] = useState<SharedProductCardProduct | null>(null);
  useEffect(() => {
    if (!productId) { setProduct(null); return; }
    retrieveProduct(productId).then((p: any) => {
      if (!p) return;
      const cap = p.variants?.[0]?.calculated_price;
      const op = p.variants?.[0]?.original_price;
      setProduct({
        id: p.id, title: p.title, handle: p.handle, thumbnail: p.thumbnail,
        images: p.images, variants: p.variants, inventory_quantity: p.inventory_quantity,
        created_at: p.created_at,
        // Required shape: a "variants[0].calculated_price.calculated_amount" path
        // is what default card checks. Pre-compute cents-aware shape so the
        // default card can render meaningfully:
      } as any);
      // Also stash cap/op for our ProductCardRenderer template path:
      // (ProductCardRenderer accepts the raw Medusa product directly via
      // `product` prop, so we don't need to reshape — just pass through).
    }).catch(() => setProduct(null));
  }, [productId]);

  const renderProduct: RenderProductCard = (p, restProps) => {
    // If the SDK returned a real Medusa product, hand it to the themed renderer.
    const raw = (restProps as any).productId ? (p as any) : null;
    return (
      <ProductCardRenderer
        product={raw || {
          id: p.id, title: p.title, thumbnail: p.thumbnail,
          images: p.images, variants: p.variants,
          inventory_quantity: p.inventory_quantity, created_at: p.created_at,
        }}
        template={{
          id: 'puck-product-card',
          name: 'Puck Product Card',
          type: 'PRODUCT_PAGE',
          layout: restProps.layout,
          imageGallery: {
            enabled: true, showSwiper: restProps.enableSwiper,
            aspectRatio: restProps.aspectRatio, borderRadius: restProps.borderRadius,
            shadow: restProps.showShadow, hoverZoom: restProps.hoverZoom,
          },
          title: {
            show: restProps.showTitle, textSize: restProps.titleSize,
            fontWeight: restProps.titleWeight, textAlign: restProps.titleAlign,
          },
          price: {
            show: restProps.showPrice, textSize: restProps.priceSize,
            priceColor: restProps.priceColor, showCompareAt: restProps.showCompareAtPrice,
            showSavingsBadge: restProps.showSavingsBadge,
          },
          badges: {
            enabled: restProps.showBadges, showSale: restProps.showSaleBadge,
            showNew: restProps.showNewBadge, showLowStock: restProps.showLowStockBadge,
            position: restProps.badgePosition,
          },
          addToCart: {
            show: restProps.showAddToCart, buttonText: restProps.buttonText,
            buttonStyle: restProps.buttonStyle, buttonSize: restProps.buttonSize,
            showIcon: restProps.showCartIcon,
          },
          styling: {
            cardRadius: restProps.cardRadius, cardBorder: restProps.cardBorder,
            cardShadow: restProps.cardShadow, cardBackground: restProps.cardBackground,
            accentColor: restProps.accentColor, fontFamily: restProps.fontFamily,
          },
        }}
      />
    );
  };

  return <SharedProductCardRender {...(props as any)} product={product} renderProduct={renderProduct} />;
};

export const ProductCard: any = { ...SharedProductCard, render: (props: any) => <ProductCardImpl {...props} /> };
export default ProductCard;
